import { Mail, Plus, RotateCcw, Save, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { useParent } from '../ParentContext';

type Kind = 'trial' | 'confirm' | 'renewal' | 'annual' | 'price' | 'receipt' | 'failed' | 'locked' | 'paused' | 'resuming' | 'refund' | 'changed' | 'cancel' | 'deleting';
interface Template {
  subject: string;
  body: string;
}
interface Rule {
  state: string;
  law: string;
  min: number;
  max: number;
  source: string;
  send: number;
  builtIn: boolean;
}
interface Notice {
  kind: Kind;
  title: string;
  when: string;
  statesOnly: boolean;
  timed: boolean;
  on: boolean;
  days: number;
  series: number[];
  rules: Rule[];
  template: Template;
  wording: Record<string, Template>;
  needs: string[];
  builtInRules: Omit<Rule, 'send' | 'builtIn'>[];
  builtInTemplate: Template;
  eyebrow: string;
  heading: string;
  cta: string;
}
interface View {
  reviewed: string;
  tokens: string[];
  notices: Notice[];
}

/** Filled in for the preview: a family in California on a yearly plan. */
const SAMPLE: Record<string, string> = {
  parent_first: 'Heather',
  family: 'Vieira family',
  plan: 'Homeschool Plan',
  amount: '$179.00',
  interval: 'year',
  trial_end: 'November 26',
  days_left: '15',
  renewal_date: 'December 2',
  cancel_by: 'December 1',
  new_amount: '$199.00',
  change_date: 'December 15',
  resume_date: 'August 1',
  refund_amount: '$19.00',
  delete_date: 'December 17',
  manage_link: 'classguardian.app/settings#plan',
  cancel_link: 'classguardian.app/settings#plan',
  support_email: 'support@classguardian.app',
  state: 'CA',
};
const fill = (t: string) => t.replace(/\{\{(\w+)\}\}/g, (all, k: string) => SAMPLE[k] ?? all);
const blank = { state: '', law: '', min: 30, max: 60, source: '' };

/**
 * Admin → Server settings → Billing notices (#871): which billing emails go out and when, by the family's billing state
 * (each state's automatic-renewal law sets a window; the admin picks a day inside it), and what each email says.
 */
export function BillingNotices() {
  const { toast } = useParent();
  const [view, setView] = useState<View | null>(null);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [kind, setKind] = useState<Kind>('trial');
  const [editing, setEditing] = useState('');
  const [adding, setAdding] = useState<typeof blank | null>(null);
  const [busy, setBusy] = useState('');
  const load = (v: View) => {
    setView(v);
    setNotices(v.notices);
  };
  useEffect(() => {
    api.get<View>('/api/admin/billing-notices').then(load, (e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  const n = notices.find((x) => x.kind === kind);
  const update = (patch: Partial<Notice>) => setNotices((list) => list.map((x) => (x.kind === kind ? { ...x, ...patch } : x)));
  const t = n ? (editing && n.wording[editing]) || n.template : null;
  const setTemplate = (next: Template) => {
    if (!n) return;
    if (editing) update({ wording: { ...n.wording, [editing]: next } });
    else update({ template: next });
  };
  const missing = useMemo(() => (n && t ? n.needs.filter((k) => !t.body.includes(`{{${k}}}`)) : []), [n, t]);

  if (!view || !n || !t) return <p className="muted small">Loading…</p>;

  /** What changed from the built-in, for the server. */
  const choices = () =>
    Object.fromEntries(
      notices.map((x) => [
        x.kind,
        {
          on: x.on,
          ...(x.timed && !x.series.length ? { days: x.days } : {}),
          ...(x.series.length ? { series: x.series } : {}),
          send: Object.fromEntries(x.rules.filter((r) => r.max > 0).map((r) => [r.state, r.send])),
          rules: x.rules.filter((r) => !r.builtIn).map(({ state, law, min, max, source }) => ({ state, law, min, max, source })),
          ...(x.template.subject !== x.builtInTemplate.subject || x.template.body !== x.builtInTemplate.body ? { template: x.template } : {}),
          wording: x.wording,
        },
      ]),
    );
  async function save() {
    setBusy('save');
    try {
      load(await api.put<View>('/api/admin/billing-notices', choices()));
      toast({ text: 'Billing notices saved', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy('');
    }
  }
  async function restore() {
    setBusy('restore');
    try {
      load(await api.post<View>('/api/admin/billing-notices/restore', { kind }));
      setEditing('');
      toast({ text: `${n!.title}: built-in rules and email restored`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy('');
    }
  }
  async function test() {
    setBusy('test');
    try {
      const r = await api.post<{ to: string }>('/api/admin/billing-notices/test', { kind, state: editing || undefined });
      toast({ text: `Sent a test to ${r.to}. Save first to test unsaved changes.`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy('');
    }
  }
  function addRule() {
    if (!adding) return;
    const state = adding.state.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(state)) return toast({ text: 'Use the state’s two letters, like IL.', tone: 'alert' });
    if (n!.rules.some((r) => r.state === state)) return toast({ text: `${state} already has a rule.`, tone: 'alert' });
    const timed = n!.timed;
    const r: Rule = { ...adding, state, min: timed ? adding.min : 0, max: timed ? adding.max : 0, send: timed ? Math.min(adding.max, Math.max(adding.min, n!.days)) : 0, builtIn: false };
    update({ rules: [...n!.rules, r].sort((a, b) => a.state.localeCompare(b.state)) });
    setAdding(null);
  }
  const ownStates = Object.keys(n.wording);

  return (
    <div className="stack bn">
      <p className="muted small">
        The emails families get about paying, sent from your server’s email. The renewal reminder follows each family’s <strong>billing state</strong> (from
        the address on their Stripe billing): its automatic-renewal law sets a window, and you pick a day inside it. A family with no billing address yet
        gets a day inside every state’s window. Built-in rules last reviewed {view.reviewed}; they’re a starting point, not legal advice.
      </p>

      <div className="bn-layout">
        <nav className="bn-list" aria-label="Notices">
          {notices.map((x) => (
            <button key={x.kind} type="button" className={x.kind === kind ? 'on' : ''} onClick={() => (setKind(x.kind), setEditing(''), setAdding(null))}>
              <span className="bn-name">{x.title}</span>
              <span className={`bn-pill ${x.on ? 'on' : ''}`}>{x.on ? 'On' : 'Off'}</span>
              <span className="muted small bn-when">
                {x.series.length ? `${x.series.join(', ')} days before the trial ends` : x.timed ? `${x.days} days before` : x.statesOnly ? 'Yearly, listed states' : 'Right away'}
                {x.rules.length ? ` · ${x.rules.length} state rule${x.rules.length === 1 ? '' : 's'}` : ''}
              </span>
            </button>
          ))}
        </nav>

        <div className="stack bn-main">
          <div className="row-between wrap">
            <h3>{n.title}</h3>
            <label className="check">
              <input type="checkbox" checked={n.on} onChange={(e) => update({ on: e.target.checked })} /> Send this notice
            </label>
          </div>
          <p className="muted small">{n.when}</p>
          {n.kind === 'trial' && (
            <p className="muted small">
              Account setup, not a renewal: no card is on file during the trial and nothing is charged by itself, so state renewal rules don’t apply. Goes
              to families on a trial that haven’t chosen how to pay, and stops when they do.
            </p>
          )}

          {n.series.length > 0 ? (
            <div className="row-gap wrap bn-series">
              <span className="small">Send</span>
              {n.series.map((d, i) => (
                <span key={i} className="row-gap">
                  <input
                    type="number"
                    min={1}
                    max={120}
                    value={d}
                    aria-label={`Reminder ${i + 1}, days before`}
                    onChange={(e) => update({ series: n.series.map((x, j) => (j === i ? Number(e.target.value) || 1 : x)) })}
                  />
                  <button type="button" className="icon-btn" aria-label="Remove this reminder" onClick={() => update({ series: n.series.filter((_, j) => j !== i) })}>
                    <X size={14} />
                  </button>
                </span>
              ))}
              <span className="muted small">days before the trial ends</span>
              {n.series.length < 6 && (
                <button type="button" className="btn small" onClick={() => update({ series: [...n.series, 1] })}>
                  <Plus size={14} /> Another reminder
                </button>
              )}
            </div>
          ) : n.timed ? (
            <label className="row-gap small bn-days">
              Every other state:
              <input type="number" min={1} max={120} value={n.days} onChange={(e) => update({ days: Number(e.target.value) || 1 })} /> days before
            </label>
          ) : null}

          {n.kind !== 'trial' && (
            <section className="stack">
              <div className="row-between wrap">
                <h4>By state</h4>
                {!adding && (
                  <button type="button" className="btn small" onClick={() => setAdding({ ...blank })}>
                    <Plus size={14} /> Add a state
                  </button>
                )}
              </div>
              {adding && (
                <div className="bn-add row-gap wrap">
                  <input aria-label="State" placeholder="IL" maxLength={2} value={adding.state} onChange={(e) => setAdding({ ...adding, state: e.target.value })} className="bn-st" />
                  {n.timed && (
                    <>
                      <input type="number" aria-label="At least, days before" value={adding.min} onChange={(e) => setAdding({ ...adding, min: Number(e.target.value) || 0 })} />
                      <span className="small">to</span>
                      <input type="number" aria-label="At most, days before" value={adding.max} onChange={(e) => setAdding({ ...adding, max: Number(e.target.value) || 0 })} />
                      <span className="small">days before</span>
                    </>
                  )}
                  <input aria-label="What the law requires" placeholder="What the law requires" value={adding.law} onChange={(e) => setAdding({ ...adding, law: e.target.value })} />
                  <input aria-label="Source" placeholder="Statute or source" value={adding.source} onChange={(e) => setAdding({ ...adding, source: e.target.value })} />
                  <button type="button" className="btn small primary" onClick={addRule}>
                    Add
                  </button>
                  <button type="button" className="btn small" onClick={() => setAdding(null)}>
                    Cancel
                  </button>
                </div>
              )}
              {n.statesOnly && <p className="muted small">Only families whose billing state is listed here get this notice.</p>}
              {n.rules.length ? (
                <div className="table-scroll">
                  <table className="bn-table">
                    <thead>
                      <tr>
                        <th>State</th>
                        <th>The law requires</th>
                        <th>Send</th>
                        <th>Check</th>
                        <th>Own wording</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {n.rules.map((r) => {
                        const timed = n.timed && r.max > 0;
                        const ok = !timed || (r.send >= r.min && r.send <= r.max);
                        const own = !!n.wording[r.state];
                        return (
                          <tr key={r.state}>
                            <td>
                              <strong>{r.state}</strong>
                            </td>
                            <td>
                              {r.law}
                              <span className="muted small block">{r.source}</span>
                            </td>
                            <td className="nowrap">
                              {timed ? (
                                <>
                                  <input
                                    type="number"
                                    min={r.min}
                                    max={r.max}
                                    value={r.send}
                                    aria-label={`${r.state}, days before`}
                                    onChange={(e) => update({ rules: n.rules.map((x) => (x.state === r.state ? { ...x, send: Number(e.target.value) || 0 } : x)) })}
                                  />{' '}
                                  days before
                                </>
                              ) : (
                                'Right away'
                              )}
                            </td>
                            <td>
                              <span className={`bn-pill ${ok ? 'on' : 'warn'}`}>{ok ? (timed ? `Within ${r.min}–${r.max}` : 'OK') : `Outside ${r.min}–${r.max}`}</span>
                            </td>
                            <td>
                              <label className="check small">
                                <input
                                  type="checkbox"
                                  checked={own}
                                  onChange={(e) => {
                                    const w = { ...n.wording };
                                    if (e.target.checked) w[r.state] = { ...n.template };
                                    else delete w[r.state];
                                    update({ wording: w });
                                    setEditing(e.target.checked ? r.state : editing === r.state ? '' : editing);
                                  }}
                                />{' '}
                                {r.state} wording
                              </label>
                            </td>
                            <td>
                              {!r.builtIn && (
                                <button type="button" className="icon-btn" aria-label={`Remove ${r.state}`} onClick={() => update({ rules: n.rules.filter((x) => x.state !== r.state) })}>
                                  <X size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="muted small">{n.statesOnly ? 'No states listed, so nobody gets it.' : `No state rules: every state gets it ${n.timed ? `${n.days} days before` : 'right away'}.`}</p>
              )}
            </section>
          )}

          <section className="stack">
            <div className="row-between wrap">
              <h4>The email</h4>
              {ownStates.length > 0 && (
                <div className="segmented" role="radiogroup" aria-label="Which wording">
                  {['', ...ownStates].map((s) => (
                    <button key={s || 'all'} type="button" role="radio" aria-checked={editing === s} className={editing === s ? 'active' : ''} onClick={() => setEditing(s)}>
                      {s || 'Every state'}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="bn-editor">
              <div className="stack">
                <label>
                  Subject
                  <input value={t.subject} maxLength={200} onChange={(e) => setTemplate({ ...t, subject: e.target.value })} />
                </label>
                <label>
                  Body
                  <textarea rows={11} value={t.body} maxLength={5000} onChange={(e) => setTemplate({ ...t, body: e.target.value })} />
                </label>
                <div className="bn-tokens" aria-label="Fields you can use">
                  {view.tokens.map((k) => (
                    <button key={k} type="button" onClick={() => setTemplate({ ...t, body: `${t.body}{{${k}}}` })}>
                      {`{{${k}}}`}
                    </button>
                  ))}
                </div>
                {missing.length ? (
                  <p className="bn-missing small" role="alert">
                    The body needs {missing.map((k) => `{{${k}}}`).join(', ')} before it can be saved.
                  </p>
                ) : (
                  <p className="muted small">Includes everything this notice has to say.</p>
                )}
              </div>
              <div className="bn-mail" aria-label="Preview">
                <span className="muted small">
                  Subject: <strong>{fill(t.subject)}</strong>
                </span>
                <div className="bn-mail-card">
                  <div className="bn-mail-head">
                    <span>{n.eyebrow}</span>
                    <strong>{fill(n.heading)}</strong>
                  </div>
                  <div className="bn-mail-body">{fill(t.body)}</div>
                  <span className="bn-mail-cta">{n.cta}</span>
                </div>
              </div>
            </div>
          </section>

          <div className="row-gap bn-actions">
            <button type="button" className="btn primary" onClick={save} disabled={!!busy || missing.length > 0}>
              <Save size={16} /> {busy === 'save' ? 'Saving…' : 'Save billing notices'}
            </button>
            <button type="button" className="btn" onClick={test} disabled={!!busy}>
              <Mail size={16} /> {busy === 'test' ? 'Sending…' : editing ? `Send me the ${editing} version` : 'Send me a test'}
            </button>
            <button type="button" className="btn" onClick={restore} disabled={!!busy}>
              <RotateCcw size={16} /> Restore built-in for this notice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
